import {
  type AssetPositionGroup,
  type Scheme,
  useGetSchemeGroupsSchemeSchemeIdGroupsGet,
  useGetSchemeListSchemeGet,
} from "kubb";
import { useLocation } from "react-router-dom";
import { selectPropertyFromLocationHash } from "../components/navigation/URL";
import { useSelectedCompany } from "./useCompany";

export const SCHEME_LIST_MAX = 1000;
const STALE_TIME = 5 * 60 * 1000;

// Module-level constants so the "nothing selected" results keep a stable
// reference across renders (avoids spurious effect/memo re-runs in consumers).
const EMPTY_SCHEMES: Scheme[] = [];
const EMPTY_GROUPS: AssetPositionGroup[] = [];

// Scheme list is scoped to the selected company. With no company selected the
// query stays disabled and consumers see an empty list (the old reducer
// behaviour). TanStack owns the data, invalidation and refetch.
function useSchemeListQuery() {
  const company = useSelectedCompany();
  return useGetSchemeListSchemeGet(
    { company_id: company?.company_id as string, max_items: SCHEME_LIST_MAX },
    {
      query: {
        enabled: Boolean(company),
        staleTime: STALE_TIME,
      },
    },
  );
}

export function useSchemes(): Scheme[] {
  const company = useSelectedCompany();
  const { data } = useSchemeListQuery();
  if (!company) {
    return EMPTY_SCHEMES;
  }
  return data?.data ?? EMPTY_SCHEMES;
}

// For components that need the loading/error state directly (the scheme selector
// and the gate that blocks the admin layout while the list loads).
export function useSchemesQueryState(): {
  schemes: Scheme[];
  loading: boolean;
  error: Error | null;
} {
  const company = useSelectedCompany();
  const { data, isPending, error } = useSchemeListQuery();
  if (!company) {
    return { schemes: EMPTY_SCHEMES, loading: false, error: null };
  }
  return {
    schemes: data?.data ?? EMPTY_SCHEMES,
    loading: isPending,
    error: error ?? null,
  };
}

// Raw scheme id from the URL hash — may reference a scheme that isn't in the
// current company's list (stale/deep-linked); used only to resolve the selection.
function useSchemeIdFromHash(): string | undefined {
  const location = useLocation();
  return selectPropertyFromLocationHash("scheme", location.hash);
}

// Selection is derived purely from the URL hash + the current list. An unknown
// or stale scheme_id (e.g. left over after switching company) resolves to
// undefined rather than throwing.
export function useSelectedScheme(): Scheme | undefined {
  const id = useSchemeIdFromHash();
  const schemes = useSchemes();
  if (!id) {
    return undefined;
  }
  return schemes.find((scheme) => scheme.scheme_id === id);
}

// The id of the *resolved* selected scheme — undefined when the hash points at a
// scheme not in the current company's list. Matches the old reducer hook, which
// validated the id against the loaded list; consumers feed this straight into
// API filters, so it must not leak a stale/cross-company id.
export function useSelectedSchemeId(): string | undefined {
  return useSelectedScheme()?.scheme_id;
}

function useGroupsQuery() {
  const scheme = useSelectedScheme();
  return useGetSchemeGroupsSchemeSchemeIdGroupsGet(
    scheme?.scheme_id as string,
    { company_id: scheme?.company_id as string },
    {
      query: {
        enabled: Boolean(scheme),
        staleTime: STALE_TIME,
      },
    },
  );
}

// Always returns an array (never undefined while a scheme's groups are loading),
// so synchronous reads at mount — e.g. a useState initializer — see a stable []
// instead of undefined. The old layout blocked render until groups loaded, which
// guaranteed an array; this preserves that contract without the blocking gate.
export function useAssetPositionGroups(): AssetPositionGroup[] {
  const scheme = useSelectedScheme();
  const { data } = useGroupsQuery();
  if (!scheme) {
    return EMPTY_GROUPS;
  }
  return data?.data ?? EMPTY_GROUPS;
}

// Mirrors the old SchemeContext.state.groups shape for the AssetPositionGroup
// selectors that read loading/error/groups together.
export function useAssetPositionGroupsState(): {
  loading: boolean;
  groups?: AssetPositionGroup[];
  error?: Error;
} {
  const scheme = useSelectedScheme();
  const { data, isPending, error } = useGroupsQuery();
  if (!scheme) {
    return { loading: false, groups: EMPTY_GROUPS };
  }
  return {
    loading: isPending,
    groups: data?.data,
    error: error ?? undefined,
  };
}
